/**
 * Connector adapter infrastructure types.
 *
 * Connectors are external backends (databases, KV stores, object stores,
 * in-process state, git repos, etc.). A connector exposes up to two faces: a
 * `ToolFace` (agents access it through injected tools) and/or a
 * `FilesystemFace` (the connector projects content into a mounted directory).
 *
 * @docLink packages/connectors/concepts#connector-types
 */

import type {
  ConnectorContent,
  ConnectorEntry,
  ListOptions,
  OperationDescriptor,
  SearchOptions,
  SearchResult,
  SyncOptions,
  SyncResult,
  WatchHandle,
  WatchOptions,
} from "./shared-types.js";

export type { OperationDescriptor } from "./shared-types.js";

import type { Logger } from "@skaile/workspaces/types";
import type { SecretProvider } from "./secrets.js";

// ── Connector declaration (runtime config, resolved from skaile.yaml) ────────

/**
 * Parsed connector configuration from `skaile.yaml`, passed to `ConnectorAdapter.connect()` and held by `ConnectorManager`.
 * @docLink packages/connectors/concepts#connector-declaration
 */
export interface ConnectorDeclaration {
  /** Unique identifier for this connector instance. */
  id: string;
  /** Driver type: "postgres", "redis", "memory", "xstate", "yjs", etc. */
  driver: string;
  /**
   * Human-readable connection name, projected from the materialized CONNECTOR.md
   * frontmatter `label`. The platform resource-explorer tab renders this over the
   * driver name when present; absent for connectors with no configured name.
   */
  label?: string;
  /** Access level enforced by the manager. */
  access: "read-only" | "read-write";
  /** Credential reference: "env:VAR_NAME" or inline value. */
  auth?: string;
  /**
   * Backend provider link id for `auth: "backend"` connectors. The runner's
   * token-refresh mediator passes this as the `host.refresh_credential` `id` so
   * the platform resolves the credential against the right provider link.
   */
  providerLinkId?: string;
  /**
   * Operating mode for connectors that manage a working copy. Orthogonal to
   * faces. `agent` = the agent drives the source natively (e.g. native git) and
   * the connector only provisions short-lived credentials. `sync` = the
   * connector owns the working copy (clone + watch + auto-commit/pull). Absent =
   * the driver's legacy `options.{session,sync,lifecycle}` blocks decide.
   */
  mode?: "agent" | "sync";
  /** Driver-specific configuration. */
  options?: Record<string, unknown>;
  /**
   * Owner-authored instruction text projected from the materialized
   * CONNECTOR.md body (preset custom instructions). Injected eagerly into the
   * agent system prompt. Absent when the body is empty.
   */
  instructions?: string;
  /** Filesystem-face config — only meaningful for mountable connectors. */
  mount?: {
    /** Source URL or path (repo URL, bucket name, host path). */
    source: string;
    /** Override mount target directory (default: `.mounts/<id>/`). */
    target?: string;
    /** Watch flag: omitted → watch read-write, skip read-only. */
    watch?: boolean;
    /** Expert-only: expose the resolved credential to the agent CLI. */
    exposeAccessToken?: boolean;
    /** Optional access-token TTL override (seconds). */
    accessTokenTTL?: number;
  };
}

// ── Connector handle (returned by adapter.connect) ───────────────────────────

/**
 * Live state of a connected backend, returned by `Connector.connect()` (or the
 * legacy `ConnectorAdapter.connect()`) and held by `ConnectorManager`.
 * @docLink packages/connectors/concepts#connector-handle
 */
export interface ConnectorHandle {
  /** Connector declaration ID. */
  readonly id: string;
  /** Driver name. */
  readonly driver: string;
  /** Effective access level. */
  readonly access: "read-only" | "read-write";
  /** Lifecycle status. Set by every `Connector`. */
  status: "connecting" | "connected" | "error" | "disconnected";
  /** Present iff the connector has a filesystem face and is mounted. */
  mountPath?: string;
  /** Adapter-specific state (connection pool, client, etc.). Opaque to callers. */
  state: unknown;
}

/** Optional capability a filesystem-face connector attaches to its handle state
 * when reads need an authoritative remote byte size rather than trusting local
 * filesystem metadata.
 */
export interface AuthoritativeSizeProvider {
  authoritativeSize(relPath: string): Promise<number | null>;
}

// ── Connector change event ────────────────────────────────────────────────────

/**
 * Change event emitted by a connector adapter's `watch()` implementation.
 * @docLink packages/connectors/concepts#connector-change-event
 */
export interface ConnectorChangeEvent {
  /** Relative path, key, table name, node ID, etc. */
  path: string;
  /** What happened. */
  action: "create" | "edit" | "delete" | "conflict";
  /** What caused the change. */
  source: "filesystem" | "operation" | "remote" | "sync";
}

// ── ConnectorManager info ────────────────────────────────────────────────────

/**
 * Summary of a connected connector returned by `ConnectorManager.listConnectors()`.
 * @docLink packages/connectors/concepts#connector-info
 */
export interface ConnectorInfo {
  id: string;
  driver: string;
  /** Human-readable connection name; the resource-explorer tab renders this over `id`/`driver` when present. */
  label?: string;
  access: "read-only" | "read-write";
  /** Operation names this connector exposes as agent tools (via describeOperations). */
  operations: string[];
}

/**
 * Bulk connect/disconnect result returned by `ConnectorManager.connectAll()` and `disconnectAll()`.
 * @docLink packages/connectors/concepts#connector-report
 */
export interface ConnectorReport {
  results: Array<{ id: string; connected: boolean; error?: string }>;
}

// ── Field spec (connector credential/config declarations) ────────────────────

/**
 * Input types for `ConnectorFieldSpec`. Controls validation rules and the Forge UI affordance.
 * @docLink packages/connectors/concepts#field-type
 */
export type FieldType =
  | "text" // plain string
  | "password" // sensitive; always stored via SecretProvider
  | "api-key" // alias for password with specific UI affordance
  | "url" // validated URL string
  | "oauth" // triggers OAuth flow; oauthService required
  | "select" // enum; choices required and non-empty
  | "boolean" // default must be "true" or "false" if provided
  | "number"; // default must be a parseable numeric string if provided

/**
 * Declares one credential or configuration field that a connector adapter requires.
 * Used by `ConnectorManager.resolveFields()` and the Forge settings UI.
 * @docLink packages/connectors/concepts#connector-field-spec
 */
export interface ConnectorFieldSpec {
  /** Machine name used as the options key in skaile.yaml. */
  key: string;
  /** Human-readable label for the forge settings UI. */
  label: string;
  /** Tooltip / help text. */
  description?: string;
  /** Input type. Controls validation rules and UI affordance in the forge settings panel. */
  type: FieldType;
  /** Whether the adapter refuses to connect when this field is missing. */
  required: boolean;
  /** String fallback used when not provided and not required. */
  default?: string;
  /** Forge UI masks the value; value is never logged. */
  sensitive: boolean;
  /** UI grouping: "Authentication", "Connection", "Options". */
  group?: string;
  /**
   * Required when type === "select"; must be non-empty.
   */
  choices?: string[];
  /**
   * Required when type === "oauth".
   * Identifies the OAuth service, e.g. "google".
   * Used to call OAuthSecretProvider.initiateFlow(oauthService, account).
   * oauth fields must be declared AFTER any field they reference (e.g., "account").
   */
  oauthService?: string;
  /** Example value shown as placeholder text in the forge settings UI. */
  example?: string;
}

/**
 * Thrown by `ConnectorManager.connectAll()` and `resolveFields()` when a required
 * adapter field cannot be resolved through the `SecretProviderChain`. Carries the
 * connector ID, the missing field spec, the ref that was tried, and the available
 * providers for actionable error messages.
 * @docLink packages/connectors/concepts#connector-field-missing-error
 */
export class ConnectorFieldMissingError extends Error {
  readonly connectorId: string;
  readonly field: ConnectorFieldSpec;
  readonly triedRef: string;
  readonly availableProviders: string[];

  constructor(opts: {
    connectorId: string;
    field: ConnectorFieldSpec;
    triedRef: string;
    availableProviders: string[];
  }) {
    super(
      `Connector "${opts.connectorId}" is missing required field "${opts.field.key}" ` +
        `(tried: ${opts.triedRef || "(none)"}). ` +
        `Available providers: ${opts.availableProviders.join(", ") || "(none)"}`,
    );
    this.name = "ConnectorFieldMissingError";
    this.connectorId = opts.connectorId;
    this.field = opts.field;
    this.triedRef = opts.triedRef;
    this.availableProviders = opts.availableProviders;
  }
}

// ── Runtime skill descriptor ──────────────────────────────────────────────────

/**
 * Instance-specific skill descriptor returned by `ConnectorAdapter.describeSkill()`.
 * Returned by `ConnectorManager.listSkills()` to surface connector capabilities to the agent.
 * @docLink packages/connectors/concepts#runtime-skill-descriptor
 */
export interface RuntimeSkillDescriptor {
  /** Connector instance id, e.g. "gmail-work". */
  id: string;
  /** Short label: "Gmail — work@company.com". */
  name: string;
  /** Instance-specific description: "Read and write email for work@company.com". */
  description: string;
  /** Category for grouping in the forge UI. */
  category: string;
  access: "read-only" | "read-write";
  /** Operation names (without loading full defs) for agent planning. */
  toolNames: string[];
}

// ── Token mediator (Tier-2 credential mediation) ─────────────────────────────

/**
 * Result of a single `TokenMediator` call. Mirrors
 * `AccessTokenResponseEvent.result` from `@skaile/workspaces/types` so the runner
 * can pass it straight through.
 *
 * @docLink packages/connectors/api-reference#token-mediator
 */
export type TokenMediatorResult =
  | {
      ok: true;
      /** Access token to use for the next git operation. */
      token: string;
      /**
       * ISO-8601 expiry, or `null` for indefinite tokens (static PATs). Drives
       * whether the driver schedules a refresh tick.
       */
      expiresAt: string | null;
      /** Optional scope/permissions list, surfaced in audit logs. */
      scope?: string[];
    }
  | {
      ok: false;
      code: "not-configured" | "revoked" | "provider-error" | "backend-error" | "timeout";
      /** Human-readable detail, never carries secrets. */
      message: string;
    };

/**
 * Function signature mountable connectors use to acquire a fresh access token
 * from the runner-owned backend mediator. Implemented by the runner: emits a
 * `request_access_token` command over the transport and resolves with the
 * matching `access_token_response` payload.
 *
 * Connectors MUST treat this as the only token source when the parsed
 * `MountAuthRef.kind === 'backend'`. Local minting (PAT, OAuth, GitHub App)
 * has been removed from the runner — provider dispatch lives on the platform.
 *
 * @docLink packages/connectors/api-reference#token-mediator
 */
export type TokenMediator = (input: {
  connectorId: string;
  reason: "initial" | "refresh" | "retry-401";
  /**
   * Connector driver, used by the runner mediator to pick the platform refresh
   * `kind`; absent on `initial` and for legacy callers.
   */
  driver?: string;
  /**
   * Backend provider link id — the `id` the platform `host.refresh_credential`
   * capability resolves the credential against. Absent on `initial` and for
   * legacy callers.
   */
  providerLinkId?: string;
}) => Promise<TokenMediatorResult>;

// ── Unified connector types ───────────────────────────────────────────────────

/**
 * Context passed to `Connector.connect()` by `ConnectorManager`.
 * @docLink packages/connectors/concepts#connect-context
 */
export interface ConnectContext {
  /** Secret provider for resolving credential references. */
  secrets?: SecretProvider;
  /**
   * Backend token mediator — present for `auth: backend` connectors of either
   * face (filesystem or tool), so mount refresh and tool-face OAuth flows share
   * one path.
   */
  tokenMediator?: TokenMediator;
  /**
   * Absolute directory the manager allocated for the filesystem face.
   * Present iff `connector.filesystem != null`. The connector projects its
   * content here during `connect()` and sets `handle.mountPath`.
   */
  mountTarget?: string;
  /** Resolved credential/config fields (from `describeFields()`). */
  resolvedFields?: Record<string, string>;
  /**
   * Aborted when the owning `ConnectorManager` is disposed. Long-running
   * `connect()` implementations — notably background (deferred) filesystem
   * mounts — should observe this and bail out promptly so session cleanup
   * does not wait on a wedged remote.
   */
  abortSignal?: AbortSignal;
}

/**
 * Filesystem face — present ⇒ the connector is "mountable".
 * @docLink packages/connectors/concepts#filesystem-face
 */
export interface FilesystemFace {
  /** Pull latest remote state into the mounted directory. */
  sync(handle: ConnectorHandle, options?: SyncOptions): Promise<SyncResult>;
}

/**
 * Tool face — present ⇒ the connector exposes agent tools.
 * @docLink packages/connectors/concepts#tool-face
 */
export interface ToolFace {
  read?(handle: ConnectorHandle, path: string): Promise<ConnectorContent | null>;
  write?(handle: ConnectorHandle, path: string, content: ConnectorContent): Promise<void>;
  delete?(handle: ConnectorHandle, path: string): Promise<boolean>;
  list?(handle: ConnectorHandle, path?: string, options?: ListOptions): Promise<ConnectorEntry[]>;
  search?(handle: ConnectorHandle, query: string, options?: SearchOptions): Promise<SearchResult[]>;
  describeOperations(handle: ConnectorHandle): OperationDescriptor[];
  executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string>;
  describeSkill?(
    handle: ConnectorHandle,
    declaration: ConnectorDeclaration,
  ): RuntimeSkillDescriptor;
}

/**
 * The unified connector contract. A connector should expose at least one face
 * (`filesystem` and/or `tools`); `ConnectorManager` rejects a faceless
 * connector at connect time.
 * @docLink packages/connectors/concepts#connector
 */
export interface Connector {
  readonly name: string;
  connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle>;
  disconnect(handle: ConnectorHandle): Promise<void>;
  readonly filesystem?: FilesystemFace;
  readonly tools?: ToolFace;
  describeFields?(): ConnectorFieldSpec[];
  setLogger?(logger: Logger): void;
  watch?(
    handle: ConnectorHandle,
    callback: (e: ConnectorChangeEvent) => void,
    options?: WatchOptions,
  ): WatchHandle;
  onHibernate?(handle: ConnectorHandle): Promise<string | undefined>;
  onSessionClose?(handle: ConnectorHandle): Promise<string | undefined>;
}

/**
 * Factory used by the connector registry.
 * @docLink packages/connectors/api-reference#connector-factory
 */
export type ConnectorFactory = () => Connector;

/**
 * Per-connector bulk-connect result returned by `ConnectorManager.connectAll()`.
 * @docLink packages/connectors/concepts#connector-startup-report
 */
export interface ConnectorStartupReport {
  results: Array<{
    id: string;
    connected: boolean;
    mountPath?: string;
    error?: string;
    access?: "read-only" | "read-write";
  }>;
}

/**
 * A connector that failed to connect, as returned by
 * `ConnectorManager.listFailedConnectors()`.
 *
 * A failed connector never enters the manager's active map, so it is absent
 * from `listConnectors()` and `get()` by design — those describe what the agent
 * can actually use. This record is the second list that keeps the failure
 * itself visible.
 *
 * @docLink packages/connectors/concepts#connector-manager
 */
export interface FailedConnectorInfo {
  id: string;
  driver: string;
  access: "read-only" | "read-write";
  label?: string;
  /** `mount.source` when declared. */
  source?: string;
  /** Resolved absolute mount target, when the declaration has a mount. */
  mountPath?: string;
  /** True when the failure is fail-fast material: read-write AND mount-bearing. */
  required: boolean;
  /** The connect error message. */
  error: string;
}

/**
 * Thrown by `connectAll` when a read-write mountable connector fails and
 * `failOnReadWriteError` was set.
 * @docLink packages/connectors/concepts#connector-startup-error
 */
export class ConnectorStartupError extends Error {
  readonly report: ConnectorStartupReport;
  readonly failedReadWrite: ReadonlyArray<{ id: string; error: string }>;
  constructor(
    message: string,
    report: ConnectorStartupReport,
    failedReadWrite: ReadonlyArray<{ id: string; error: string }>,
  ) {
    super(message);
    this.name = "ConnectorStartupError";
    this.report = report;
    this.failedReadWrite = failedReadWrite;
  }
}
